/**
 * Markdown content loader.
 *
 * Reads .md files from src/content/ (via raw-loader), parses YAML-ish
 * frontmatter, and exposes structured data for the page components.
 * Content is the single source of truth — edit the .md files, not JSX.
 */
import aboutMd from "!!raw-loader!../content/about.md"; // eslint-disable-line import/no-webpack-loader-syntax
import contactMd from "!!raw-loader!../content/contact.md"; // eslint-disable-line import/no-webpack-loader-syntax
import jishMd from "!!raw-loader!../content/projects/jish.md"; // eslint-disable-line import/no-webpack-loader-syntax
import tanbanMd from "!!raw-loader!../content/projects/tanban.md"; // eslint-disable-line import/no-webpack-loader-syntax
import bruhdleMd from "!!raw-loader!../content/projects/bruhdle.md"; // eslint-disable-line import/no-webpack-loader-syntax
import trickMd from "!!raw-loader!../content/projects/trick.md"; // eslint-disable-line import/no-webpack-loader-syntax
import phazeMd from "!!raw-loader!../content/projects/phaze.md"; // eslint-disable-line import/no-webpack-loader-syntax
import jostleMd from "!!raw-loader!../content/experience/jostle.md"; // eslint-disable-line import/no-webpack-loader-syntax

/* ---------- Simple frontmatter parser ---------- */

function parseFrontmatter(raw) {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n|^---\n([\s\S]*?)\n---\s*$/);
  if (!match) return { metadata: {}, content: raw.trim() };

  const rawMeta = match[1] ?? match[2] ?? "";
  const content = raw.slice(match[0].length).trim();

  const metadata = {};
  for (const line of rawMeta.split("\n")) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    if (value.startsWith("[") && value.endsWith("]")) {
      value = value
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim());
    }
    metadata[key] = value;
  }
  return { metadata, content };
}

/** Split a project document into sections by `## Heading` markers. */
function parseSections(content) {
  const sections = {};
  const lines = content.split("\n");
  let current = null;

  for (const line of lines) {
    const m = line.match(/^## (.+)$/);
    if (m) {
      current = slugify(m[1]);
      sections[current] = "";
      continue;
    }
    if (current) sections[current] += line + "\n";
  }
  return sections;
}

/** Convert a heading like "Lessons Learned" into a stable id "lessons-learned". */
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/* ---------- Parse all documents ---------- */

const about = parseFrontmatter(aboutMd);
const contact = parseFrontmatter(contactMd);

const PROJECT_FILES = {
  phaze: phazeMd,
  jish: jishMd,
  tanban: tanbanMd,
  bruhdle: bruhdleMd,
  trick: trickMd,
};

const EXPERIENCE_FILES = {
  jostle: jostleMd,
};

const projects = Object.entries(PROJECT_FILES).map(([slug, raw]) => {
  const { metadata, content } = parseFrontmatter(raw);
  return {
    slug,
    title: metadata.title || slug,
    tagline: metadata.tagline || "",
    tags: Array.isArray(metadata.tags) ? metadata.tags : [],
    description: metadata.description || "",
    hero: metadata.hero || "",
    content,
    sections: parseSections(content),
  };
});

const experiences = Object.entries(EXPERIENCE_FILES).map(([slug, raw]) => {
  const { metadata, content } = parseFrontmatter(raw);
  return {
    slug,
    title: metadata.title || slug,
    company: metadata.company || "",
    date: metadata.date || "",
    logo: metadata.logo || "",
    tags: Array.isArray(metadata.tags) ? metadata.tags : [],
    content,
  };
});

/* ---------- Public API ---------- */

export const getAbout = () => ({ ...about, title: "about.md" });
export const getContact = () => ({ ...contact, title: "contact.md" });

export const getAllProjects = () => projects;
export const getProject = (slug) =>
  projects.find((p) => p.slug === slug) || null;

/** Section list derived from the project document's `##` headings. */
export const getProjectSections = (project) =>
  Object.keys(project.sections).map((id) => ({
    id,
    label: id
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
  }));

export const getAllExperiences = () => experiences;
export const getExperience = (slug) =>
  experiences.find((e) => e.slug === slug) || null;
